/*
  <home-carousel> and <home-video> — shared by the home-* sections
  (home-banner-block, home-collection-carousel, home-stl-carousel, home-ugc-carousel).

  <home-carousel> wraps a native horizontal scroller (.home-carousel__track, CSS scroll-snap).
  It deliberately avoids the issues documented in product-carousel.liquid for Swiper:
  - Android: no JS touch handling at all. Touch swipes are the browser's own native scroll,
    so vertical page scroll is never fought over.
  - macOS Safari trackpad: no wheel listener. Inertial vertical scroll can't be misread as
    horizontal and freeze the page; horizontal trackpad gestures scroll the track natively.
  - iOS hairlines: slides are never translated; widths are whole pixels (home-sections.css).
  - Taps: clicks are only suppressed after a *mouse* drag of more than 5px.

  The element only acts while its track actually overflows (e.g. the Banner Block is a
  grid from tablet up and the element stays inert there).
*/

(() => {
  const DRAG_THRESHOLD = 5;
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  if (!customElements.get('home-carousel')) {
    class HomeCarousel extends HTMLElement {
      constructor() {
        super();
        this.onScroll = this.onScroll.bind(this);
        this.onKeydown = this.onKeydown.bind(this);
        this.onPointerDown = this.onPointerDown.bind(this);
        this.onPointerMove = this.onPointerMove.bind(this);
        this.onPointerUp = this.onPointerUp.bind(this);
        this.onClickCapture = this.onClickCapture.bind(this);
        this.onDragStart = this.onDragStart.bind(this);
        this.onBlockSelect = this.onBlockSelect.bind(this);
        this.onPrev = () => this.step(-1);
        this.onNext = () => this.step(1);
      }

      connectedCallback() {
        this.track = this.querySelector('.home-carousel__track');
        if (!this.track) return;

        this.prevButton = this.querySelector('[data-home-carousel-prev]');
        this.nextButton = this.querySelector('[data-home-carousel-next]');
        this.regionLabel = this.track.getAttribute('aria-label');

        this.track.addEventListener('scroll', this.onScroll, { passive: true });
        this.track.addEventListener('keydown', this.onKeydown);
        this.track.addEventListener('pointerdown', this.onPointerDown);
        this.track.addEventListener('click', this.onClickCapture, true);
        this.track.addEventListener('dragstart', this.onDragStart);
        this.addEventListener('shopify:block:select', this.onBlockSelect);
        this.prevButton?.addEventListener('click', this.onPrev);
        this.nextButton?.addEventListener('click', this.onNext);

        this.resizeObserver = new ResizeObserver(() => this.update());
        this.resizeObserver.observe(this.track);
        this.update();
      }

      disconnectedCallback() {
        if (!this.track) return;
        this.track.removeEventListener('scroll', this.onScroll);
        this.track.removeEventListener('keydown', this.onKeydown);
        this.track.removeEventListener('pointerdown', this.onPointerDown);
        this.track.removeEventListener('click', this.onClickCapture, true);
        this.track.removeEventListener('dragstart', this.onDragStart);
        this.removeEventListener('shopify:block:select', this.onBlockSelect);
        this.prevButton?.removeEventListener('click', this.onPrev);
        this.nextButton?.removeEventListener('click', this.onNext);
        this.stopDragListeners();
        this.resizeObserver?.disconnect();
        cancelAnimationFrame(this.frame);
        clearTimeout(this.clickTimer);
      }

      get slides() {
        return Array.from(this.track.children).filter((el) => el.classList.contains('home-carousel__slide'));
      }

      get maxScroll() {
        return Math.max(0, this.track.scrollWidth - this.track.clientWidth);
      }

      isScrollable() {
        return getComputedStyle(this.track).overflowX !== 'visible' && this.maxScroll > 1;
      }

      update() {
        const scrollable = this.isScrollable();
        this.classList.toggle('is-scrollable', scrollable);

        if (scrollable) {
          this.track.setAttribute('role', 'region');
          this.track.setAttribute('aria-roledescription', 'carousel');
          if (this.regionLabel) this.track.setAttribute('aria-label', this.regionLabel);
          this.track.setAttribute('tabindex', '0');
        } else {
          this.track.removeAttribute('role');
          this.track.removeAttribute('aria-roledescription');
          this.track.removeAttribute('aria-label');
          this.track.removeAttribute('tabindex');
        }

        this.updateArrows();
      }

      updateArrows() {
        if (!this.prevButton || !this.nextButton) return;
        const left = this.track.scrollLeft;
        this.prevButton.disabled = left <= 1;
        this.nextButton.disabled = left >= this.maxScroll - 1;
      }

      onScroll() {
        cancelAnimationFrame(this.frame);
        this.frame = requestAnimationFrame(() => this.updateArrows());
      }

      nearestIndex() {
        const left = this.track.scrollLeft;
        let nearest = 0;
        let distance = Infinity;
        this.slides.forEach((slide, index) => {
          const d = Math.abs(slide.offsetLeft - left);
          if (d < distance) {
            distance = d;
            nearest = index;
          }
        });
        return nearest;
      }

      goTo(index, instant = false) {
        const slides = this.slides;
        const slide = slides[Math.max(0, Math.min(index, slides.length - 1))];
        if (!slide) return;
        this.track.scrollTo({
          left: Math.min(slide.offsetLeft, this.maxScroll),
          behavior: instant || reducedMotion.matches ? 'auto' : 'smooth',
        });
      }

      step(direction) {
        const left = this.track.scrollLeft;
        const offsets = this.slides.map((slide) => Math.min(slide.offsetLeft, this.maxScroll));
        let target;
        if (direction > 0) {
          target = offsets.find((offset) => offset > left + 1);
        } else {
          target = [...offsets].reverse().find((offset) => offset < left - 1);
        }
        if (target === undefined) return;
        this.track.scrollTo({ left: target, behavior: reducedMotion.matches ? 'auto' : 'smooth' });
      }

      onKeydown(event) {
        if (!this.isScrollable() || event.altKey || event.ctrlKey || event.metaKey) return;
        if (event.key === 'ArrowRight') {
          event.preventDefault();
          this.step(1);
        } else if (event.key === 'ArrowLeft') {
          event.preventDefault();
          this.step(-1);
        }
      }

      onPointerDown(event) {
        if (event.pointerType !== 'mouse' || event.button !== 0 || !this.isScrollable()) return;
        this.drag = { startX: event.clientX, startLeft: this.track.scrollLeft, moved: false };
        window.addEventListener('pointermove', this.onPointerMove);
        window.addEventListener('pointerup', this.onPointerUp);
        window.addEventListener('pointercancel', this.onPointerUp);
      }

      onPointerMove(event) {
        if (!this.drag) return;
        const dx = event.clientX - this.drag.startX;
        if (!this.drag.moved && Math.abs(dx) > DRAG_THRESHOLD) {
          this.drag.moved = true;
          this.track.classList.add('is-dragging');
        }
        if (this.drag.moved) this.track.scrollLeft = this.drag.startLeft - dx;
      }

      onPointerUp() {
        this.stopDragListeners();
        if (this.drag?.moved) {
          this.track.classList.remove('is-dragging');
          this.goTo(this.nearestIndex());
          // The click that follows this mouseup must not follow a link. It fires in the
          // same task, so the flag is cleared right after in case no click comes.
          this.suppressClick = true;
          clearTimeout(this.clickTimer);
          this.clickTimer = setTimeout(() => {
            this.suppressClick = false;
          }, 0);
        }
        this.drag = null;
      }

      stopDragListeners() {
        window.removeEventListener('pointermove', this.onPointerMove);
        window.removeEventListener('pointerup', this.onPointerUp);
        window.removeEventListener('pointercancel', this.onPointerUp);
      }

      onClickCapture(event) {
        if (!this.suppressClick) return;
        event.preventDefault();
        event.stopPropagation();
        this.suppressClick = false;
      }

      onDragStart(event) {
        // Native image/link dragging would hijack the mouse drag-to-scroll.
        event.preventDefault();
      }

      onBlockSelect(event) {
        const slide = event.target.closest('.home-carousel__slide');
        if (!slide || !this.isScrollable()) return;
        const index = this.slides.indexOf(slide);
        if (index > -1) this.goTo(index, true);
      }
    }

    customElements.define('home-carousel', HomeCarousel);
  }

  if (!customElements.get('home-video')) {
    /*
      Plays a muted, looping tile video only while it is in the viewport (hidden breakpoint
      variants never intersect, so they never load). The video gets its src on first view
      and fades in over the tile image once it is actually playing. Nothing loads or plays
      under prefers-reduced-motion: the image stays.
    */
    class HomeVideo extends HTMLElement {
      constructor() {
        super();
        this.sync = this.sync.bind(this);
        this.onPlaying = () => this.classList.add('is-playing');
      }

      connectedCallback() {
        this.video = this.querySelector('video');
        if (!this.video || !('IntersectionObserver' in window)) return;

        this.video.muted = true;
        this.video.addEventListener('playing', this.onPlaying);
        reducedMotion.addEventListener('change', this.sync);

        this.observer = new IntersectionObserver(
          (entries) => {
            this.inView = entries[entries.length - 1].isIntersecting;
            this.sync();
          },
          { threshold: 0.2 }
        );
        this.observer.observe(this);
      }

      disconnectedCallback() {
        this.observer?.disconnect();
        reducedMotion.removeEventListener('change', this.sync);
        if (this.video) {
          this.video.removeEventListener('playing', this.onPlaying);
          this.video.pause();
        }
      }

      sync() {
        if (!this.video) return;
        if (this.inView && !reducedMotion.matches) {
          if (!this.video.getAttribute('src') && this.video.dataset.src) {
            this.video.src = this.video.dataset.src;
          }
          const playing = this.video.play();
          if (playing) playing.catch(() => {});
        } else if (!this.video.paused) {
          this.video.pause();
        }
      }
    }

    customElements.define('home-video', HomeVideo);
  }
})();
